import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertCircle,
  IndianRupee,
  Download,
  Filter,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Car,
  User,
  Calendar,
  Phone,
} from "lucide-react";

export const DriversAffectedByCancellations = ({ isCityAdmin = false }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [cities, setCities] = useState([]);
  const [drivers, setDrivers] = useState([]);
  
  // Filters
  const [cityFilter, setCityFilter] = useState("all");
  const [driverFilter, setDriverFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [page, setPage] = useState(1);
  const [viewMode, setViewMode] = useState("drivers"); // "drivers" or "rides"

  useEffect(() => {
    if (user?.id) {
      if (!isCityAdmin) fetchCities();
      fetchDrivers();
      fetchData();
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id) {
      fetchData();
    }
  }, [cityFilter, driverFilter, dateFrom, dateTo, page]);

  const fetchCities = async () => {
    try {
      const res = await axios.get(`${API}/cities?admin_id=${user.id}`);
      // Sort cities alphabetically
      const sortedCities = (res.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setCities(sortedCities);
    } catch (err) {
      console.error("Failed to fetch cities", err);
    }
  };

  const fetchDrivers = async () => {
    try {
      const endpoint = isCityAdmin 
        ? `${API}/city-admin/${user.id}/all-drivers`
        : `${API}/admin/drivers?admin_id=${user.id}`;
      const res = await axios.get(endpoint);
      // City Admin endpoint returns {drivers: [...], total: N}, Admin endpoint returns array directly
      const driversData = isCityAdmin ? (res.data?.drivers || []) : (res.data || []);
      setDrivers(driversData);
    } catch (err) {
      console.error("Failed to fetch drivers", err);
    }
  };

  const fetchData = async () => {
    if (!user?.id) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: page.toString(), page_size: "50" });
      if (!isCityAdmin && cityFilter && cityFilter !== "all") {
        params.append("city_id", cityFilter);
      }
      if (driverFilter && driverFilter !== "all") {
        params.append("driver_id", driverFilter);
      }
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      
      const endpoint = isCityAdmin
        ? `${API}/city-admin/${user.id}/drivers-affected-by-cancellations`
        : `${API}/admin/${user.id}/drivers-affected-by-cancellations`;
      
      const res = await axios.get(`${endpoint}?${params.toString()}`);
      setData(res.data);
    } catch (err) {
      console.error("Error:", err);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams({ export_format: format });
      if (!isCityAdmin && cityFilter && cityFilter !== "all") {
        params.append("city_id", cityFilter);
      }
      if (driverFilter && driverFilter !== "all") {
        params.append("driver_id", driverFilter);
      }
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      
      const endpoint = isCityAdmin
        ? `${API}/city-admin/${user.id}/drivers-affected-by-cancellations`
        : `${API}/admin/${user.id}/drivers-affected-by-cancellations`;
      
      const res = await axios.get(`${endpoint}?${params.toString()}`, {
        responseType: format === "csv" ? "blob" : "json"
      });
      
      if (format === "csv") {
        const blob = new Blob([res.data], { type: "text/csv" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "drivers_affected_by_cancellations.csv";
        a.click();
        window.URL.revokeObjectURL(url);
      } else {
        const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "drivers_affected_by_cancellations.json";
        a.click();
        window.URL.revokeObjectURL(url);
      }
      
      toast.success(`Exported as ${format.toUpperCase()}`);
    } catch (err) {
      toast.error("Failed to export");
    }
  };

  const clearFilters = () => {
    setCityFilter("all");
    setDriverFilter("all");
    setDateFrom("");
    setDateTo("");
    setPage(1);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="drivers-affected-by-cancellations">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-amber-500" />
            Drivers Affected by Customer Cancellations
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Drivers who had accepted rides before customers cancelled
          </p>
        </div>
        
        {/* Export Buttons */}
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => handleExport("csv")}>
            <Download className="w-4 h-4 mr-1" />
            CSV
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport("json")}>
            <Download className="w-4 h-4 mr-1" />
            JSON
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      {data?.summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2 text-amber-700">
              <Car className="w-5 h-5" />
              <span className="text-sm font-medium">Drivers Affected</span>
            </div>
            <p className="text-3xl font-bold text-amber-800 mt-2">
              {data.summary.total_drivers_affected}
            </p>
          </div>
          <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-2 text-red-700">
              <AlertCircle className="w-5 h-5" />
              <span className="text-sm font-medium">Total Cancellations</span>
            </div>
            <p className="text-3xl font-bold text-red-800 mt-2">
              {data.summary.total_cancellations}
            </p>
          </div>
          <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg">
            <div className="flex items-center gap-2 text-orange-700">
              <IndianRupee className="w-5 h-5" />
              <span className="text-sm font-medium">Total Lost Fare</span>
            </div>
            <p className="text-3xl font-bold text-orange-800 mt-2">
              ₹{data.summary.total_lost_fare?.toLocaleString()}
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="p-4 bg-muted/50 rounded-lg border">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="font-medium">Filters</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {/* City Filter - Only for Admin */}
          {!isCityAdmin && cities.length > 0 && (
            <div>
              <Label className="text-xs text-muted-foreground">City</Label>
              <Select value={cityFilter} onValueChange={(v) => { setCityFilter(v); setPage(1); }}>
                <SelectTrigger data-testid="city-filter">
                  <SelectValue placeholder="All Cities" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Cities</SelectItem>
                  {cities.map((city) => (
                    <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {/* Driver Filter */}
          <div>
            <Label className="text-xs text-muted-foreground">Driver</Label>
            <Select value={driverFilter} onValueChange={(v) => { setDriverFilter(v); setPage(1); }}>
              <SelectTrigger data-testid="driver-filter">
                <SelectValue placeholder="All Drivers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Drivers</SelectItem>
                {drivers.map((driver) => (
                  <SelectItem key={driver.id} value={driver.id}>
                    {driver.name || driver.phone}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          
          {/* Date From */}
          <div>
            <Label className="text-xs text-muted-foreground">From Date</Label>
            <Input
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
              data-testid="date-from"
            />
          </div>
          
          {/* Date To */}
          <div>
            <Label className="text-xs text-muted-foreground">To Date</Label>
            <Input
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
              data-testid="date-to"
            />
          </div>
          
          {/* Clear Filters */}
          <div className="flex items-end">
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear Filters
            </Button>
          </div>
        </div>
      </div>

      {/* View Toggle */}
      <div className="flex gap-2">
        <Button
          variant={viewMode === "drivers" ? "default" : "outline"}
          size="sm"
          onClick={() => setViewMode("drivers")}
        >
          <Car className="w-4 h-4 mr-1" />
          By Driver
        </Button>
        <Button
          variant={viewMode === "rides" ? "default" : "outline"}
          size="sm"
          onClick={() => setViewMode("rides")}
        >
          <Calendar className="w-4 h-4 mr-1" />
          All Rides
        </Button>
      </div>

      {/* Drivers Table */}
      {viewMode === "drivers" && (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Driver</TableHead>
                {!isCityAdmin && <TableHead>City</TableHead>}
                <TableHead className="text-center">Cancellations</TableHead>
                <TableHead className="text-right">Lost Fare</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.drivers?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={isCityAdmin ? 3 : 4} className="text-center py-8 text-muted-foreground">
                    No affected drivers found with current filters
                  </TableCell>
                </TableRow>
              ) : (
                data?.drivers?.map((driver, idx) => (
                  <TableRow key={driver.driver_id || idx}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center">
                          <User className="w-4 h-4 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium">{driver.driver_name}</p>
                          <p className="text-xs text-muted-foreground">{driver.driver_phone}</p>
                        </div>
                      </div>
                    </TableCell>
                    {!isCityAdmin && (
                      <TableCell className="text-muted-foreground">
                        {driver.city_name || "-"}
                      </TableCell>
                    )}
                    <TableCell className="text-center">
                      <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-sm font-medium">
                        {driver.cancellation_count}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-medium text-orange-600">
                      ₹{driver.total_lost_fare?.toLocaleString()}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* All Rides Table */}
      {viewMode === "rides" && (
        <div className="border rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Driver</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Accepted At</TableHead>
                <TableHead>Cancelled At</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead className="text-right">Est. Fare</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data?.all_rides?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No cancelled rides found with current filters
                  </TableCell>
                </TableRow>
              ) : (
                data?.all_rides?.map((ride, idx) => (
                  <TableRow key={ride.ride_id || idx}>
                    <TableCell>
                      <p className="font-medium">{ride.driver_name}</p>
                      <p className="text-xs text-muted-foreground">{ride.driver_phone}</p>
                    </TableCell>
                    <TableCell>
                      <p className="font-medium">{ride.customer_name}</p>
                      <p className="text-xs text-muted-foreground">{ride.customer_phone}</p>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {ride.accepted_at ? new Date(ride.accepted_at).toLocaleString('en-IN', {
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                      }) : '-'}
                    </TableCell>
                    <TableCell className="text-xs text-red-600">
                      {ride.cancelled_at ? new Date(ride.cancelled_at).toLocaleString('en-IN', {
                        month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                      }) : '-'}
                    </TableCell>
                    <TableCell className="max-w-[150px]">
                      <span className="text-xs text-muted-foreground truncate block">
                        {ride.cancellation_reason || "Not specified"}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-medium text-orange-600">
                      ₹{Math.round(ride.estimated_fare || 0)}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Pagination */}
      {data?.pagination && data.pagination.total_pages > 1 && viewMode === "drivers" && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Page {data.pagination.page} of {data.pagination.total_pages} ({data.pagination.total} total)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= data.pagination.total_pages}
              onClick={() => setPage(page + 1)}
            >
              Next
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DriversAffectedByCancellations;
